import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CircleDollarSign, ArrowLeft, ShieldAlert, Loader2, PauseCircle, PlayCircle, XCircle, ShieldCheck, Lock, Plus } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import DisposicionesTab from "@/components/lineas/DisposicionesTab";
import HistorialLineaTab from "@/components/lineas/HistorialLineaTab";

const ESTATUS_STYLES = {
  "Autorizada": "bg-emerald-100 text-emerald-700",
  "Vigente": "bg-sky-100 text-sky-700",
  "Suspendida": "bg-amber-100 text-amber-700",
  "Vencida": "bg-slate-200 text-slate-700",
  "Cancelada": "bg-rose-100 text-rose-700",
};

const fmtDinero = (n) =>
  typeof n === "number"
    ? n.toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 })
    : "—";

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso;
  }
};

function Dato({ label, value, testid }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800" data-testid={testid}>{value ?? "—"}</dd>
    </>
  );
}

export default function LineaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const esAdmin = user?.role === "admin_entidad" || user?.role === "super_admin";
  const [linea, setLinea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogo, setDialogo] = useState(null); // "suspender" | "cancelar" | null
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  // Fase 6 (feb-2026) — Estado de bloqueo por mora + lista de garantías de la línea.
  // Se cargan en paralelo con el detalle; reusan endpoints response-only.
  const [bloqueo, setBloqueo] = useState(null);
  const [garantias, setGarantias] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lineas/${id}`);
      setLinea(data);
      setError("");
      // Fase 6 (feb-2026) — carga en paralelo del estado de bloqueo y las garantías
      // de la línea. Los endpoints son response-only; fallar en cualquiera de ellos
      // NO debe romper la vista principal del detalle.
      try {
        const [b, g] = await Promise.all([
          api.get(`/lineas/${id}/bloqueo`).then((r) => r.data).catch(() => null),
          api.get(`/lineas/${id}/garantias`).then((r) => r.data).catch(() => []),
        ]);
        setBloqueo(b);
        setGarantias(Array.isArray(g) ? g : []);
      } catch { /* silencioso */ }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const suspender = async () => {
    if (!motivo.trim()) { toast.error("Captura un motivo"); return; }
    setBusy(true);
    try {
      await api.post(`/lineas/${id}/suspender`, { motivo: motivo.trim() });
      toast.success("Línea suspendida.");
      setDialogo(null); setMotivo("");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reactivar = async () => {
    setBusy(true);
    try {
      await api.post(`/lineas/${id}/reactivar`);
      toast.success("Línea reactivada.");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cancelar = async () => {
    if (!motivo.trim()) { toast.error("Captura un motivo"); return; }
    setBusy(true);
    try {
      await api.post(`/lineas/${id}/cancelar`, { motivo: motivo.trim() });
      toast.success("Línea cancelada.");
      setDialogo(null); setMotivo("");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="linea-detalle-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando línea…
      </div>
    );
  }

  if (error || !linea) {
    return (
      <div className="p-6" data-testid="linea-detalle-error">
        <Button variant="outline" size="sm" onClick={() => navigate("/lineas")} data-testid="linea-volver">
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800">
          {error || "Línea no encontrada."}
        </div>
      </div>
    );
  }

  const estatusCls = ESTATUS_STYLES[linea.estatus] || "bg-slate-100 text-slate-700";

  return (
    <div className="p-6 space-y-6" data-testid="linea-detalle">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate("/lineas")} data-testid="linea-volver">
          <ArrowLeft className="mr-2 h-4 w-4" /> Todas las líneas
        </Button>
        <div className="flex items-center gap-2">
          {esAdmin && ["Autorizada", "Vigente"].includes(linea.estatus) && (
            <Button variant="outline" size="sm" onClick={() => setDialogo("suspender")} data-testid="linea-btn-suspender">
              <PauseCircle className="mr-2 h-4 w-4" /> Suspender
            </Button>
          )}
          {esAdmin && linea.estatus === "Suspendida" && (
            <Button variant="outline" size="sm" onClick={reactivar} disabled={busy} data-testid="linea-btn-reactivar">
              <PlayCircle className="mr-2 h-4 w-4" /> Reactivar
            </Button>
          )}
          {esAdmin && linea.estatus !== "Cancelada" && (
            <Button variant="outline" size="sm" onClick={() => setDialogo("cancelar")} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="linea-btn-cancelar">
              <XCircle className="mr-2 h-4 w-4" /> Cancelar
            </Button>
          )}
        </div>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {linea.numero_linea}
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${estatusCls}`} data-testid="linea-estatus">
              {linea.estatus}
            </span>
          </span>
        }
        description={<span className="text-slate-600">{linea.cliente} · {linea.producto}</span>}
        icon={CircleDollarSign}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatCard label="Límite autorizado" value={fmtDinero(Number(linea.limite_autorizado))} testid="stat-limite" />
        <StatCard label="Dispuesto" value={fmtDinero(Number(linea.dispuesto))} testid="stat-dispuesto" />
        <StatCard label="Disponible" value={fmtDinero(Number(linea.disponible))} testid="stat-disponible" />
        <StatCard label="Disposiciones vigentes" value={linea.num_disposiciones_vigentes ?? 0} testid="stat-num-disposiciones" />
      </div>

      {/* Fase 6 (feb-2026) — Banner de bloqueo por mora. Visible SIEMPRE que
          `bloqueo.bloqueada === true` (independientemente de la tab activa). Para
          que el operador NO tenga que intentar disponer para descubrir el bloqueo. */}
      {bloqueo?.bloqueada && (
        <div
          className="flex items-start gap-3 rounded-lg border-2 border-rose-300 bg-rose-50 p-4"
          data-testid="linea-banner-bloqueo"
        >
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-rose-700" />
          <div className="flex-1 text-sm text-rose-800">
            <p className="font-semibold">Línea bloqueada para nuevas disposiciones</p>
            <p className="mt-1" data-testid="linea-banner-motivo">{bloqueo.motivo}</p>
            <p className="mt-1 text-xs text-rose-700">
              Regulariza el pago de la disposición afectada para reactivar la línea.
            </p>
          </div>
        </div>
      )}

      {/* Fase 6 (feb-2026) — Aviso persistente de cobertura insuficiente sellado en la
          propia línea. Sobrevive al refresh (a diferencia del toast de la garantía);
          rastro auditable de que el comité aprobó con cobertura parcial. */}
      {linea.cobertura_aviso_actual && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
          data-testid="linea-banner-cobertura"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div className="flex-1 text-xs text-amber-800">
            <p className="font-semibold">Cobertura de garantía insuficiente ({linea.cobertura_aviso_actual.cobertura_pct}%)</p>
            <p className="mt-0.5">{linea.cobertura_aviso_actual.mensaje}</p>
            <p className="mt-0.5 text-[10px] text-amber-700">
              Registrado el {fmtFecha(linea.cobertura_aviso_actual.registrado_at)} por {linea.cobertura_aviso_actual.registrado_por || "—"}
            </p>
          </div>
        </div>
      )}

      <Tabs defaultValue="condiciones">
        <TabsList>
          <TabsTrigger value="condiciones" data-testid="linea-tab-condiciones">Condiciones selladas</TabsTrigger>
          <TabsTrigger value="disposiciones" data-testid="linea-tab-disposiciones">Disposiciones</TabsTrigger>
          <TabsTrigger value="garantia" data-testid="linea-tab-garantia">Garantía</TabsTrigger>
          <TabsTrigger value="historial" data-testid="linea-tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="condiciones" className="mt-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Instrumento</p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                <Dato label="Número de línea" value={<span className="font-mono">{linea.numero_linea}</span>} testid="cond-numero" />
                <Dato label="Cliente" value={linea.cliente} testid="cond-cliente" />
                <Dato label="Producto" value={linea.producto} testid="cond-producto" />
                <Dato label="Solicitud" value={linea.solicitud_id?.slice(0, 8)} testid="cond-solicitud" />
                <Dato label="Aprobada por" value={linea.aprobada_por} testid="cond-aprobada-por" />
                <Dato label="Fecha aprobación" value={fmtFecha(linea.fecha_aprobacion)} testid="cond-fecha-aprobacion" />
              </dl>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Límites y vigencia</p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                <Dato label="Límite autorizado" value={fmtDinero(Number(linea.limite_autorizado))} testid="cond-limite" />
                <Dato label="Vigencia" value={`${linea.vigencia_meses} meses`} testid="cond-vigencia" />
                <Dato label="Fecha inicio" value={fmtFecha(linea.fecha_inicio)} testid="cond-fecha-inicio" />
                <Dato label="Fecha vencimiento" value={fmtFecha(linea.fecha_vencimiento)} testid="cond-fecha-vencimiento" />
                <Dato label="Disposición mínima" value={fmtDinero(Number(linea.disposicion_min))} testid="cond-dispo-min" />
                <Dato label="Disposición máxima" value={fmtDinero(Number(linea.disposicion_max))} testid="cond-dispo-max" />
                <Dato label="Plazo máx. por disposición" value={`${linea.plazo_max_disposicion} periodos`} testid="cond-plazo-max" />
              </dl>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Tasas y régimen</p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                <Dato label="Tasa ordinaria" value={`${Number(linea.tasa_ordinaria).toFixed(2)}%`} testid="cond-tasa-ord" />
                <Dato label="Tasa moratoria" value={`${Number(linea.tasa_moratoria).toFixed(2)}%`} testid="cond-tasa-mora" />
                <Dato label="Periodicidad" value={linea.periodicidad} testid="cond-periodicidad" />
                <Dato label="Amortización" value={linea.tipo_amortizacion} testid="cond-amortizacion" />
                <Dato label="IVA intereses" value={linea.iva_intereses_credito} testid="cond-iva-intereses" />
              </dl>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Comisiones y bloqueo</p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                <Dato label="Apertura de línea" value={linea.comision_apertura_linea > 0
                  ? `${linea.comision_apertura_linea}${linea.comision_apertura_linea_tipo === "porcentaje" ? "%" : " MXN"} · ${fmtDinero(Number(linea.comision_apertura_linea_monto))}`
                  : "No aplica"} testid="cond-com-apertura" />
                <Dato label="Comisión por disposición" value={linea.comision_disposicion > 0
                  ? `${linea.comision_disposicion}${linea.comision_disposicion_tipo === "porcentaje" ? "%" : " MXN"}`
                  : "No aplica"} testid="cond-com-dispo" />
                <Dato label="Bloqueo por mora" value={linea.bloqueo_mora_activo
                  ? `Activo · ${linea.bloqueo_mora_dias} días`
                  : "Inactivo"} testid="cond-bloqueo-mora" />
              </dl>
              {linea.comision_apertura_linea > 0 && (
                <p className="mt-3 rounded-md bg-amber-50 p-3 text-xs text-amber-700">
                  La comisión de apertura de línea está calculada y sellada, pero <strong>no se cobra en esta fase</strong>. El timbrado se implementa en fases posteriores.
                </p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="disposiciones" className="mt-4">
          <DisposicionesTab linea={linea} onChange={load} bloqueo={bloqueo} />
        </TabsContent>

        <TabsContent value="garantia" className="mt-4">
          <GarantiaLineaTab
            linea={linea}
            garantias={garantias}
            onChange={load}
          />
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          {/* Fase 8+ (feb-2026) — Estado de cuenta visual de la línea. Reemplaza
              al bloque estático que solo mostraba eventos de estatus por el
              componente `HistorialLineaTab` que además calcula al vuelo las
              disposiciones, desembolsos, pagos (con desglose) y disponible
              resultante. Aditivo puro: cero cambios de backend. */}
          <HistorialLineaTab linea={linea} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!dialogo} onOpenChange={(o) => { if (!o) { setDialogo(null); setMotivo(""); } }}>
        <DialogContent data-testid="linea-dialog-motivo">
          <DialogHeader>
            <DialogTitle>
              {dialogo === "suspender" ? "Suspender línea" : "Cancelar línea"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
              <ShieldAlert className="mr-2 inline h-4 w-4" />
              {dialogo === "suspender"
                ? "Una línea suspendida no permite disponer. Puedes reactivarla después."
                : "La cancelación es DEFINITIVA. La línea deja de estar activa y no se puede reactivar."}
            </div>
            <Textarea
              placeholder="Captura el motivo (requerido)…"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              data-testid="linea-motivo-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogo(null); setMotivo(""); }} data-testid="linea-motivo-cancel">Cerrar</Button>
            <Button
              onClick={dialogo === "suspender" ? suspender : cancelar}
              disabled={busy || !motivo.trim()}
              className={dialogo === "cancelar" ? "bg-rose-600 hover:bg-rose-700" : ""}
              data-testid="linea-motivo-confirm"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {dialogo === "suspender" ? "Suspender" : "Cancelar línea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase 6 (feb-2026) — Tab "Garantía" del detalle de la línea.
//
// Registra una garantía LIGADA A LA LÍNEA (endpoint POST /lineas/{id}/garantia).
// La cobertura se compara contra `linea.limite_autorizado`; si < 100% el backend
// devuelve `advertencias[]` (toast amarillo) Y sella el rastro en la propia línea
// (`cobertura_aviso_actual` + `cobertura_avisos_historial`) para trazabilidad de
// auditor. Un tipo Aval/Quirografaria no exige valor.
// ─────────────────────────────────────────────────────────────────────────────
const _TIPOS_GARANTIA_LINEA = [
  { value: "Prendaria", label: "Prendaria (con valor)" },
  { value: "Hipotecaria", label: "Hipotecaria (con valor)" },
  { value: "Fiduciaria", label: "Fiduciaria (con valor)" },
  { value: "Líquida", label: "Líquida (con valor)" },
  { value: "Aval", label: "Aval (sin valor)" },
  { value: "Quirografaria", label: "Quirografaria (sin valor)" },
];

function GarantiaLineaTab({ linea, garantias, onChange }) {
  const [tipo, setTipo] = useState("Prendaria");
  const [valor, setValor] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [avaluo, setAvaluo] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [busy, setBusy] = useState(false);

  const exigeValor = ["Prendaria", "Hipotecaria", "Fiduciaria", "Líquida"].includes(tipo);

  const submit = async () => {
    if (exigeValor) {
      const v = parseFloat(valor);
      if (!v || v <= 0) { toast.error("Captura el valor comercial o de avalúo (> 0)"); return; }
    }
    setBusy(true);
    try {
      const payload = {
        tipo_garantia: tipo,
        valor_garantia: exigeValor ? parseFloat(valor) : 0,
        descripcion, avaluo, observaciones,
      };
      const { data } = await api.post(`/lineas/${linea.id}/garantia`, payload);
      toast.success(`Garantía ${tipo} registrada por ${fmtDinero(data.garantia?.valor_garantia || 0)}`);
      (data.advertencias || []).forEach((a) => toast.warning(a.mensaje, { duration: 10000 }));
      setValor(""); setDescripcion(""); setAvaluo(""); setObservaciones("");
      await onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2" data-testid="garantia-linea-form">
        <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" />
          Registrar garantía sobre la línea
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Tipo de garantía</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="h-9 text-sm" data-testid="garantia-linea-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {_TIPOS_GARANTIA_LINEA.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {exigeValor && (
            <div>
              <Label className="text-xs">Valor comercial (MXN)</Label>
              <Input type="number" value={valor} onChange={(e) => setValor(e.target.value)}
                className="h-9 text-sm" placeholder={String(linea.limite_autorizado)}
                data-testid="garantia-linea-valor" />
              <p className="mt-1 text-[10px] text-slate-500">
                Cobertura ideal: al menos {fmtDinero(Number(linea.limite_autorizado))} (100% del límite).
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs">Descripción</Label>
            <Input value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
              className="h-9 text-sm" placeholder="Ej. Escritura pública No. 12345"
              data-testid="garantia-linea-descripcion" />
          </div>
          {exigeValor && (
            <div>
              <Label className="text-xs">Referencia del avalúo</Label>
              <Input value={avaluo} onChange={(e) => setAvaluo(e.target.value)}
                className="h-9 text-sm" placeholder="Ej. Perito No. 5678 · 15-ene-2026"
                data-testid="garantia-linea-avaluo" />
            </div>
          )}
          <div>
            <Label className="text-xs">Observaciones</Label>
            <Textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
              className="min-h-[60px] text-sm" placeholder="Notas del comité, condiciones especiales, etc."
              data-testid="garantia-linea-obs" />
          </div>
          <Button onClick={submit} disabled={busy}
            data-testid="garantia-linea-submit" className="bg-accent hover:bg-forest-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
            Registrar garantía
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Garantías vigentes de la línea ({garantias.length})
        </p>
        {garantias.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="garantia-linea-vacia">
            No hay garantías registradas para esta línea todavía.
          </p>
        ) : (
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-1.5">Tipo</th>
                  <th className="px-3 py-1.5 text-right">Valor</th>
                  <th className="px-3 py-1.5">Descripción</th>
                  <th className="px-3 py-1.5">Registrada</th>
                  <th className="px-3 py-1.5">Estatus</th>
                </tr>
              </thead>
              <tbody>
                {garantias.map((g) => (
                  <tr key={g.id} className="border-t border-slate-100" data-testid={`garantia-linea-row-${g.id}`}>
                    <td className="px-3 py-1.5 font-medium">{g.tipo_garantia}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                      {g.valor_garantia > 0 ? fmtDinero(g.valor_garantia) : "—"}
                    </td>
                    <td className="px-3 py-1.5">{g.descripcion || "—"}</td>
                    <td className="px-3 py-1.5">{fmtFecha(g.created_at)}</td>
                    <td className="px-3 py-1.5">
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        {g.estatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
